<template>
  <v-card outlined class="mb-2">
    <v-card-text class="pa-3">
      <div class="d-flex justify-space-between align-center mb-1">
        <router-link
          :to="getAccountRoute({ name: 'availability', params: { id: item._key }})"
          class="text-decoration-none"
        >
          <strong class="primary--text text-body-1">{{ item.property.name }}</strong>
        </router-link>
        <v-chip
          small
          color="primary"
          outlined
          class="ml-2"
        >
          {{ transactionType }}
        </v-chip>
      </div>

      <v-list dense class="pa-0">
        <v-list-item class="px-0 py-1" style="min-height: 28px;">
          <v-list-item-icon class="mr-2 my-0">
            <v-icon small>mdi-map-marker</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="py-0">
            <v-list-item-title class="text-body-2">{{ getFormattedAddress(item.address) }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item v-if="item.available_space" class="px-0 py-1" style="min-height: 28px;">
          <v-list-item-icon class="mr-2 my-0">
            <v-icon small>mdi-ruler-square</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="py-0">
            <v-list-item-title class="text-body-2">{{ formattedAvailableSpace }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item v-if="item.lease && item.lease.rate" class="px-0 py-1" style="min-height: 28px;">
          <v-list-item-icon class="mr-2 my-0">
            <v-icon small>mdi-currency-usd</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="py-0">
            <v-list-item-title class="text-body-2">{{ formattedLeaseRate }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item v-if="item.sale && item.sale.asking_price" class="px-0 py-1" style="min-height: 28px;">
          <v-list-item-icon class="mr-2 my-0">
            <v-icon small>mdi-currency-usd</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="py-0">
            <v-list-item-title class="text-body-2">{{ formattedAskingPrice }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item v-if="item.date_available" class="px-0 py-1" style="min-height: 28px;">
          <v-list-item-icon class="mr-2 my-0">
            <v-icon small>mdi-calendar-check</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="py-0">
            <v-list-item-title class="text-body-2">Available: {{ formattedDateAvailable }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item v-if="item.date_on_market" class="px-0 py-1" style="min-height: 28px;">
          <v-list-item-icon class="mr-2 my-0">
            <v-icon small>mdi-calendar-clock</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="py-0">
            <v-list-item-title class="text-body-2">On Market: {{ formattedDateOnMarket }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import mixins from 'vue-typed-mixins';
import { PropType } from 'vue';

import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { IAiAvailabilityResult } from '@/bundles/AiAgent/types';
import { formatNumber, formatCurrency, formatDate } from '@/utils/filters';
import { getFormattedAddress } from '@/bundles/App/helpers/address/getFormattedAddress';

export default mixins(AccountLayoutMixin).extend({
  name: 'AvailabilityCard',

  props: {
    item: {
      type: Object as PropType<IAiAvailabilityResult>,
      required: true,
    },
  },

  computed: {
    transactionType (): string {
      const forSale = this.item.for_sale;
      const forLease = this.item.for_lease;

      if (forSale && forLease) {
        return 'For Lease/For Sale';
      } else if (forSale) {
        return 'For Sale';
      } else if (forLease) {
        return 'For Lease';
      }

      return '';
    },

    formattedAvailableSpace (): string {
      if (!this.item.available_space) return 'N/A';
      return `${formatNumber(this.item.available_space)} SF`;
    },

    formattedLeaseRate (): string {
      if (!this.item.lease?.rate) return 'N/A';
      return `Lease Rate: ${formatCurrency(this.item.lease.rate)}`;
    },

    formattedAskingPrice (): string {
      if (!this.item.sale?.asking_price) return 'N/A';
      return `Asking Price: ${formatCurrency(this.item.sale.asking_price)}`;
    },

    formattedDateAvailable (): string {
      if (!this.item.date_available) return 'N/A';
      return formatDate(this.item.date_available)!;
    },

    formattedDateOnMarket (): string {
      if (!this.item.date_on_market) return 'N/A';
      return formatDate(this.item.date_on_market)!;
    },
  },

  methods: {
    getFormattedAddress,
  }
});
</script>

<style lang="scss" scoped>
.v-list-item__icon {
  align-self: center !important;
}
</style>
